import { DownOutlined } from "@ant-design/icons";
import { Breadcrumb, Dropdown } from "antd";
import { useEffect, useRef, useState } from "react";

export interface BreadcrumbItem {
  title: string;
  onClick?: () => void;
  className?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

export default function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  const [shouldUseDropdown, setShouldUseDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const useDropdown = containerWidth < 90;
        setShouldUseDropdown(useDropdown);
      }
    };

    checkWidth();

    window.addEventListener("resize", checkWidth);

    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(checkWidth);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener("resize", checkWidth);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const createBreadcrumbItems = () => {
    if (!shouldUseDropdown || items.length <= 1) {
      return items.map((item) => ({
        title: item.title,
        onClick: item.onClick,
        className: item.className,
      }));
    }

    if (items.length === 2) {
      const firstItem = items[0];
      const secondItem = items[1];

      return [
        {
          title: (
            <Dropdown
              menu={{
                items: [
                  {
                    key: firstItem.title,
                    label: firstItem.title,
                    onClick: firstItem.onClick
                      ? ({ domEvent }) => {
                          domEvent.preventDefault();
                          domEvent.stopPropagation();
                          firstItem.onClick?.();
                        }
                      : undefined,
                  },
                  {
                    key: secondItem.title,
                    label: secondItem.title,
                    onClick: secondItem.onClick
                      ? ({ domEvent }) => {
                          domEvent.preventDefault();
                          domEvent.stopPropagation();
                          secondItem.onClick?.();
                        }
                      : undefined,
                  },
                ],
              }}
              overlayClassName="[&_.ant-dropdown-menu-item]:!py-[8px]"
              trigger={["click"]}
              placement="bottom"
            >
              <span
                className="cursor-pointer hover:text-blue-500 transition-colors inline-flex items-center gap-1"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                ... <DownOutlined className="!text-[12px]" />
              </span>
            </Dropdown>
          ),
        },
      ];
    }

    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    const middleItems = items.slice(1, -1);

    const breadcrumbItems: Array<{
      title: string | React.ReactNode;
      onClick?: () => void;
      className?: string;
    }> = [
      {
        title: firstItem.title,
        onClick: firstItem.onClick,
        className: firstItem.className,
      },
    ];

    if (middleItems.length > 0) {
      breadcrumbItems.push({
        title: (
          <Dropdown
            menu={{
              items: middleItems.map((item, index) => ({
                key: `${item.title}-${index}`,
                label: item.title,
                onClick: item.onClick
                  ? ({ domEvent }) => {
                      domEvent.preventDefault();
                      domEvent.stopPropagation();
                      item.onClick?.();
                    }
                  : undefined,
              })),
            }}
            overlayClassName="[&_.ant-dropdown-menu-item]:!py-[8px]"
            trigger={["click"]}
            placement="bottom"
          >
            <span
              className="cursor-pointer hover:text-blue-500 transition-colors inline-flex items-center gap-1"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              ... <DownOutlined className="!text-[12px]" />
            </span>
          </Dropdown>
        ),
      });
    }

    breadcrumbItems.push({
      title: lastItem.title,
      onClick: lastItem.onClick,
      className: lastItem.className,
    });

    return breadcrumbItems;
  };

  return (
    <div ref={containerRef} className="w-full overflow-hidden">
      <Breadcrumb
        separator=">"
        className={`${className} whitespace-nowrap`}
        items={createBreadcrumbItems()}
      />
    </div>
  );
}
